/**
 * Get user initials from name
 */
export function getUserInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Get display value from ServiceNow field
 * Handles both string and object formats
 */
export function getDisplayValue(field) {
  if (!field) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object") {
    return field.display_value || field.value || "";
  }
  return String(field);
}

/**
 * Get value (sys_id) from ServiceNow field
 */
export function getValue(field) {
  if (!field) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object") {
    return field.value || "";
  }
  return String(field);
}

/**
 * Map ServiceNow state codes to readable labels
 */
export function getStateLabel(stateCode, taskStates = []) {
  const state = taskStates.find((s) => s.value === String(stateCode));
  return state ? state.label : stateCode;
}

/**
 * Get state color based on state code
 */
export function getStateColor(stateCode) {
  const stateMap = {
    1: "#FFA726", // Open - Orange
    2: "#3299FF", // Work in Progress - Blue
    3: "#66BB6A", // Closed Complete - Green
    4: "#EF5350", // Closed Incomplete - Red
    "-5": "#FFCA28", // Awaiting Information - Amber
  };
  return stateMap[String(stateCode)] || "#9E9E9E"; // Default gray
}

/**
 * Get priority label and color
 */
export function getPriorityInfo(priority) {
  const priorityMap = {
    1: { label: "Critical", color: "#D32F2F" },
    2: { label: "High", color: "#F57C00" },
    3: { label: "Medium", color: "#FBC02D" },
    4: { label: "Low", color: "#388E3C" },
    5: { label: "Planning", color: "#1976D2" },
  };
  return (
    priorityMap[String(priority)] || { label: "Unknown", color: "#9E9E9E" }
  );
}

/**
 * Format date for display
 */
export function formatDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Calculate days until/overdue
 */
export function calculateDaysUntilDue(dueDateString) {
  if (!dueDateString) return null;

  try {
    const dueDate = new Date(dueDateString);
    const now = new Date();
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      days: Math.abs(diffDays),
      isOverdue: diffDays < 0,
      label:
        diffDays < 0
          ? `${Math.abs(diffDays)} days overdue`
          : diffDays === 0
          ? "Due today"
          : `Due in ${diffDays} days`,
    };
  } catch {
    return null;
  }
}

/**
 * Check if task is closed
 */
export function isTaskClosed(stateCode) {
  return ["3", "4"].includes(String(stateCode));
}

/**
 * Check if task is assigned
 */
export function isTaskAssigned(assignedTo) {
  if (!assignedTo) return false;
  if (typeof assignedTo === "string") return assignedTo.length > 0;
  if (typeof assignedTo === "object") {
    return !!(assignedTo.value || assignedTo.display_value);
  }
  return false;
}

/**
 * Parse activity log entries (comments and work notes)
 * Assumes they are newline-separated with timestamp prefix
 */
export function parseActivityLog(commentsStr, workNotesStr) {
  const activities = [];

  const parseEntries = (str, type) => {
    if (!str) return;
    const entries = str.split("\n").filter((e) => e.trim());
    entries.forEach((entry) => {
      // Try to extract timestamp and content
      // Format: "2025-12-16 10:30:00 - User: Comment text"
      const match = entry.match(
        /^(\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2})\s*-\s*(.+?):(.+)$/
      );
      if (match) {
        activities.push({
          timestamp: match[1],
          user: match[2],
          content: match[3].trim(),
          type,
        });
      } else {
        activities.push({
          timestamp: null,
          user: "System",
          content: entry.trim(),
          type,
        });
      }
    });
  };

  parseEntries(commentsStr, "comment");
  parseEntries(workNotesStr, "work_note");

  // Sort by timestamp descending (most recent first)
  return activities.sort((a, b) => {
    if (!a.timestamp) return 1;
    if (!b.timestamp) return -1;
    return new Date(b.timestamp) - new Date(a.timestamp);
  });
}

// ============================================================================
// REFACTORED FUNCTIONS - Reduced Cognitive Complexity
// ============================================================================

/**
 * Get display name from user field
 * Handles ServiceNow user reference fields
 */
export function getUserDisplayName(userField) {
  if (!userField) return "";

  if (typeof userField === "string") {
    return userField;
  }

  if (typeof userField === "object" && userField !== null) {
    return extractFieldDisplayValue(userField);
  }

  return safeStringConversion(userField);
}

/**
 * Get display name from assignment group field
 */
export function getGroupDisplayName(groupField) {
  if (!groupField) return "";

  if (typeof groupField === "string") {
    return groupField;
  }

  if (typeof groupField === "object" && groupField !== null) {
    return extractFieldDisplayValue(groupField);
  }

  return safeStringConversion(groupField);
}

// ============================================================================
// SHARED HELPER FUNCTIONS
// ============================================================================

/**
 * Extract display value from ServiceNow field object
 * Handles nested object structures
 */
function extractFieldDisplayValue(field) {
  const displayValue = extractDisplayValueProperty(field);
  if (displayValue) return displayValue;

  const value = extractValueProperty(field);
  if (value) return value;

  return "";
}

/**
 * Extract display_value property from field
 */
function extractDisplayValueProperty(field) {
  if (field.display_value === undefined || field.display_value === null) {
    return null;
  }

  // Handle nested object structure
  if (typeof field.display_value === "object" && field.display_value.value) {
    return field.display_value.value;
  }

  // Handle string value
  if (typeof field.display_value === "string") {
    return field.display_value;
  }

  return null;
}

/**
 * Extract value property from field
 */
function extractValueProperty(field) {
  if (field.value !== undefined && field.value !== null) {
    if (typeof field.value === "string") {
      return field.value;
    }
  }
  return null;
}

/**
 * Safely convert value to string
 */
function safeStringConversion(value) {
  try {
    return String(value);
  } catch {
    return "";
  }
}
